import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getOneIngredientKey } from "./useGetOneIngredient";
import { getIngredientScoresKey } from "../olapScores/useGetIngredientScores";

type UpsertIngredient = {
    ingredient: IngredientWrite;
    companyId?: number;
};
export const useUpsertIngredient = () => {
    const queryClient = useQueryClient();

    const query = useMutation({
        mutationFn: async (options: UpsertIngredient) => {
            const { ingredient, companyId } = options;

            const response =
                ingredient.id === undefined ?
                    await loggedInApiCall.post("/api/ingredients", ingredient)
                :   await loggedInApiCall.put("/api/ingredients/" + ingredient.id, ingredient, {
                        params: {
                            organization_id: companyId,
                        },
                    });

            return response;
        },
        onSuccess: (_response, options: UpsertIngredient) => {
            const { id: ingredientId } = options.ingredient;

            if (ingredientId === undefined) return;

            queryClient.refetchQueries([getOneIngredientKey, ingredientId.toString()]);
            queryClient.refetchQueries([getIngredientScoresKey, ingredientId]);
        },
    });

    return query;
};

export type IngredientWriteIngredientSupplier =
    | IRI
    | {
          "@id"?: string;
          id?: number;
          supplier?: IRI | undefined;
          managedBy?: IRI | undefined;
          unit?: number | null;
          /**
           * IRI of the per-supplier IngredientReference, or `null` to clear it.
           * Omit the field entirely to leave the existing relationship untouched.
           */
          ingredientReference?: IRI | null;
      };

export type IngredientWriteIngredientCompany =
    | IRI
    | {
          "@id"?: string;
          id?: number;
          organization: IRI;
      };

export type IngredientWrite = {
    id?: number;
    name: string | null | undefined;
    clientId: string | null | undefined;
    ingredientReferenceId: number | null | undefined;
    referenceSource?: "sure" | "foodpilot";
    organic: boolean | null | undefined;
    ingredientLine: IRI | null | undefined;
    suppliers: IngredientWriteIngredientSupplier[];
    shares?: IngredientWriteIngredientCompany[];
    ownedBy?: IRI;
    jsonAttributes?: Record<string, any>;
    tags?: Record<string, string[]>;
};
